import { type SpainClosurePeriod } from '@spendesk/bookkeeping-core-types';
import { type ValidDate } from '@spendesk/general-type-helpers/Date';
import { addDays, addYears, isValid, parseISO } from 'date-fns';

export type SanitizedSpainClosurePeriod = {
  state: SpainClosurePeriod['state'] | 'current' | 'to_process';
  from: string;
  to: string;
  id?: string;
  readyToArchive: boolean;
};

const parseDate = (value: string | undefined) => {
  if (!value) {
    return null;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

const setUtcEndOfDay = (date: Date) => {
  const clone = new Date(date);
  clone.setUTCHours(23, 59, 59, 999);
  return clone;
};

const setUtcStartOfDay = (date: Date) => {
  const clone = new Date(date);
  clone.setUTCHours(0, 0, 0, 0);
  return clone;
};

const getFiscalYearEndDate = (
  year: number,
  fiscalYearEndMonthZeroBased: number,
  fiscalYearEndDay: number,
) => {
  const date = new Date(
    Date.UTC(year, fiscalYearEndMonthZeroBased, fiscalYearEndDay),
  );
  return setUtcEndOfDay(date);
};

const getFiscalYearForDate = (
  date: Date,
  fiscalYearEndMonthZeroBased: number,
  fiscalYearEndDay: number,
) => {
  const fiscalYearEnd = getFiscalYearEndDate(
    date.getUTCFullYear(),
    fiscalYearEndMonthZeroBased,
    fiscalYearEndDay,
  );
  return date.getTime() <= fiscalYearEnd.getTime()
    ? fiscalYearEnd.getUTCFullYear()
    : fiscalYearEnd.getUTCFullYear() + 1;
};

const buildFiscalPeriodRange = (
  year: number,
  fiscalYearEndMonthZeroBased: number,
  fiscalYearEndDay: number,
) => {
  const toDate = getFiscalYearEndDate(
    year,
    fiscalYearEndMonthZeroBased,
    fiscalYearEndDay,
  );
  const fiscalStartBase = addYears(toDate, -1);
  const fromDate = setUtcStartOfDay(addDays(fiscalStartBase, 1));

  return { from: fromDate.toISOString(), to: toDate.toISOString() };
};

const formatClosurePeriods = (
  closurePeriods: SpainClosurePeriod[],
): SanitizedSpainClosurePeriod[] => {
  return closurePeriods.map((period) => ({
    state: period.state,
    from: period.from.toISOString(),
    to: period.to.toISOString(),
    readyToArchive: false,
  }));
};

const validateInputs = (
  fiscalYearEndMonthZeroBased: number | null,
  fiscalYearEndDay: number | null,
  firstSpanishDprPayableDate: ValidDate | undefined,
) => {
  if (
    fiscalYearEndMonthZeroBased === null ||
    fiscalYearEndMonthZeroBased === undefined ||
    fiscalYearEndDay === null ||
    fiscalYearEndDay === undefined ||
    !firstSpanishDprPayableDate
  ) {
    return null;
  }
  const parsedDate = parseDate(firstSpanishDprPayableDate.toISOString());
  return parsedDate ?? null;
};

const periodsMatchExpectedRange = (
  period: SpainClosurePeriod,
  normalizedRange: { from: string; to: string },
) => {
  const normalizedFromDate = parseDate(normalizedRange.from);
  const normalizedToDate = parseDate(normalizedRange.to);
  const existingFromDateRaw = parseDate(period.from.toISOString());
  const existingToDateRaw = parseDate(period.to.toISOString());

  const existingFromDate = existingFromDateRaw
    ? setUtcStartOfDay(existingFromDateRaw)
    : null;
  const existingToDate = existingToDateRaw
    ? setUtcEndOfDay(existingToDateRaw)
    : null;

  return (
    normalizedFromDate &&
    normalizedToDate &&
    existingFromDate &&
    existingToDate &&
    normalizedFromDate.getTime() === existingFromDate.getTime() &&
    normalizedToDate.getTime() === existingToDate.getTime()
  );
};

const mapReadyToArchive = (
  closurePeriodsByYear: Map<number, SanitizedSpainClosurePeriod>,
  firstFiscalYear: number,
  endFiscalYear: number,
  fiscalMonth: number,
  fiscalDay: number,
  currentFiscalYear: number,
): SanitizedSpainClosurePeriod[] => {
  const sanitizedPeriods: SanitizedSpainClosurePeriod[] = [];
  let allPreviousCompleted = true;

  const archivableStates = ['to_process'];

  for (let year = firstFiscalYear; year <= endFiscalYear; year += 1) {
    const existingPeriod = closurePeriodsByYear.get(year);
    if (existingPeriod) {
      const isArchiveEligibleState = archivableStates.includes(
        existingPeriod.state,
      );
      sanitizedPeriods.push({
        ...existingPeriod,
        readyToArchive: allPreviousCompleted && isArchiveEligibleState,
      });
      if (existingPeriod.state !== 'completed') {
        allPreviousCompleted = false;
      }
      continue;
    }

    const { from, to } = buildFiscalPeriodRange(year, fiscalMonth, fiscalDay);
    sanitizedPeriods.push({
      state: year === currentFiscalYear ? 'current' : 'to_process',
      from,
      to,
      readyToArchive: allPreviousCompleted && year !== currentFiscalYear,
    });
    allPreviousCompleted = false;
  }
  return sanitizedPeriods;
};

export const sanitizePeriodsList = (
  fiscalYearEndMonthZeroBased: number | null,
  fiscalYearEndDay: number | null,
  firstSpanishDprPayableDate: ValidDate | undefined,
  closurePeriods: SpainClosurePeriod[],
): SanitizedSpainClosurePeriod[] => {
  const validatedFirstPayableDate = validateInputs(
    fiscalYearEndMonthZeroBased,
    fiscalYearEndDay,
    firstSpanishDprPayableDate,
  );
  if (!validatedFirstPayableDate) {
    return formatClosurePeriods(closurePeriods);
  }

  const fiscalMonth = fiscalYearEndMonthZeroBased as number;
  const fiscalDay = fiscalYearEndDay as number;

  const closurePeriodsByYear = new Map<number, SanitizedSpainClosurePeriod>();
  closurePeriods.forEach((period) => {
    const periodDate = parseDate(
      period.to.toISOString() ?? period.from.toISOString(),
    );
    if (!periodDate) {
      return;
    }
    const fiscalYear = getFiscalYearForDate(periodDate, fiscalMonth, fiscalDay);
    const normalizedRange = buildFiscalPeriodRange(
      fiscalYear,
      fiscalMonth,
      fiscalDay,
    );
    const shouldStorePeriod = periodsMatchExpectedRange(
      period,
      normalizedRange,
    );

    if (!shouldStorePeriod) {
      return;
    }
    closurePeriodsByYear.set(fiscalYear, {
      state: period.state,
      from: normalizedRange.from,
      to: normalizedRange.to,
      id: period.id,
      readyToArchive: false,
    });
  });

  const firstFiscalYear = getFiscalYearForDate(
    validatedFirstPayableDate,
    fiscalMonth,
    fiscalDay,
  );

  const lastClosedFiscalYear =
    closurePeriodsByYear.size > 0
      ? Math.max(...closurePeriodsByYear.keys())
      : firstFiscalYear;
  const currentFiscalYear = getFiscalYearForDate(
    new Date(),
    fiscalMonth,
    fiscalDay,
  );
  const endFiscalYear = Math.max(lastClosedFiscalYear, currentFiscalYear);

  return mapReadyToArchive(
    closurePeriodsByYear,
    firstFiscalYear,
    endFiscalYear,
    fiscalMonth,
    fiscalDay,
    currentFiscalYear,
  );
};
